import { Box, styled } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";

import { UserInteractionRequirement } from "@/components/Checkout/types";
import { SolidButton } from "@/components/StoreComponents/StoreButton";
import { ResponsiveDialog } from "@/components/StoreComponents/StoreDialogs/ResponsiveDialog";
import { Retailer } from "@/redux/types";
import {
  getEntityImage,
  MOBILE_SCREEN_SIZE,
  TABLET_SCREEN_SIZE,
} from "@brandclub/common-ui";
import { CheckoutInput } from "@/components/Checkout/components/CheckoutInput";

// import OTPVerificationInput from "../../signIn/OTPVerificationInput";

enum PopupStatus {
  OTP = "otp",
  LEAVING = "leaving",
}

const AccountInputFormContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  alignSelf: "center",
  // width: "100%",
  padding: "24px",
  height: "100%",
  maxWidth: 380,
  width: "100%",
  ".resend": {
    color: theme.palette.primary.main,
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
    marginTop: "24px",
  },

  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {},
  [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {},
  ".input-container": {
    display: "flex",
    flexDirection: "row",
    gap: "16px",
    width: "100%",
    marginTop: "24px",
    marginBottom: "24px",
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {},
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      flexDirection: "column",
      gap: "15px",
      marginTop: "24px",
      marginBottom: "24px",
    },
  },
}));

const Container = styled(motion.div)(({ theme }) => ({
  position: "relative",
  width: "100%",
  overflow: "hidden",
  display: "flex",
  color: theme.palette.primary.main,
}));

export const OTPPopup = ({
  open: showEdit,
  onClose: closeEdit,
  value: otp,
  onChange: setOtp,
  onSave: onSaveCredentials,
  retailer,
  onResend,
  resendCoolDown,
  userInteractionRequirement,
}: {
  open: boolean;
  onClose: () => void;
  value?: string;
  onChange: (otp: string) => void;
  onSave: () => void;
  retailer?: Retailer;
  onResend: () => void;
  resendCoolDown: number;
  userInteractionRequirement?: UserInteractionRequirement;
}) => {
  const [status, setStatus] = useState<PopupStatus>(PopupStatus.OTP);
  // const [previousStatus, setPreviousStatus] = useState<PopupStatus>(
  //   PopupStatus.OTP
  // );

  const onCloseEdit = () => {
    setStatus(PopupStatus.OTP);
    closeEdit();
  };

  const ResendText =
    resendCoolDown > 0 ? `Resend in ${resendCoolDown}s` : "Send a new code";

  return (
    <ResponsiveDialog open={showEdit} onClose={onCloseEdit}>
      <Container
        sx={(theme) => ({
          [theme.breakpoints.up("md")]: {
            minWidth: 580,
            maxWidth: 580,
            width: "100%",
          },
          overflowY: "auto",
          maxHeight: "100%",
        })}
      >
        <AnimatePresence mode="wait">
          <motion.div
            className="retailer-connect-login-motion-container"
            style={{
              boxSizing: "border-box",
              width: "100%",
              height: "100%",
              display: "flex",
              padding: "24px 24px",
              flexDirection: "column",
            }}
            key={status}
            animate={{ opacity: 1, x: 0 }}
            initial={{ opacity: 0, x: 20 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.15 }}
          >
            <AccountInputFormContainer
              sx={{
                padding: "24px",
              }}
              className="otp-container"
            >
              <Box
                className="retailer-connect-login-container"
                sx={{
                  maxWidth: 380,
                  display: "flex",
                  flexDirection: {
                    xs: "column",
                    sm: "column",
                  },
                  alignItems: "center",
                  justifyContent: "flex-start",
                  bgcolor: "white",
                  borderRadius: 4,
                }}
              >
                {/* Retailer Icon */}

                {retailer?.logo ? (
                  <img
                    alt="r_logo"
                    src={retailer.logo}
                    style={{
                      maxWidth: 162,
                      maxHeight: 72,
                      objectFit: "contain",
                      marginBottom: 35,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      bgcolor: "#e6f3f7",
                      borderRadius: "50%",
                      width: 72,
                      height: 72,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: "35px",
                    }}
                  >
                    <img
                      className="retailer_logo"
                      alt="r_logo"
                      style={{
                        width: "72px",
                        height: "72px",
                        objectFit: "contain",
                      }}
                      src={getEntityImage(
                        retailer?.retailerId || 0,
                        "retailer"
                      )}
                    />
                  </Box>
                )}
              </Box>
              {/* Title */}
              <Box
                sx={(theme) => ({
                  fontWeight: 700,
                  fontSize: 22,
                  marginBottom: "12px",
                  [theme.breakpoints.down("sm")]: {
                    fontSize: 18,
                    marginBottom: "8px",
                  },
                  textAlign: "center",
                })}
              >
                Verify your {retailer?.displayName ?? "retailer"} account
              </Box>
              <Box sx={{ textAlign: "center" }}>
                {userInteractionRequirement?.displayMessage ||
                  `Enter your ${retailer?.displayName ?? "retailer"} OTP code.`}
              </Box>
              <div className={"input-container"}>
                <CheckoutInput
                  sx={{
                    width: "100%",
                    maxWidth: 380,
                  }}
                  label={"Verification code"}
                  onChange={(e) => setOtp(e.target.value)}
                  value={otp}
                />

                {/* <OTPVerificationInput
                value={otp ?? ""}
                onChange={(value) => {
                  setOtp(value);
                }}
                maxLength={6}
                error={false}
                disabled={false}
              /> */}
              </div>
              <SolidButton
                sx={{
                  width: "100%",
                  height: "50px",
                }}
                onClick={onSaveCredentials}
                disabled={!otp}
              >
                Submit
              </SolidButton>
              <div
                className="resend"
                role="button"
                onClick={resendCoolDown > 0 ? undefined : onResend}
              >
                {ResendText}
              </div>
            </AccountInputFormContainer>
          </motion.div>
        </AnimatePresence>
      </Container>
    </ResponsiveDialog>
  );
};
